import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import styles from './BlogListPage.module.css';
import { resolveLoadErrorMessage } from '../../utils/errorMessages';

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await api.get('/blogs');
      setPosts(res.posts || []);
    } catch (error) {
      console.error('Failed to load blog posts:', error);
      // Do NOT let a FAILURE fall through to the empty list: `posts.length === 0`
      // renders "No blog posts yet. Check back soon.", which tells a reader the
      // catalogue is empty when we simply could not reach it. Record the failure
      // so the page can say so and offer a retry instead.
      setPosts([]);
      setLoadError(resolveLoadErrorMessage(error, 'We could not load the blog posts.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.heroSection}>
        <h1 className={styles.heroTitle}>Blog</h1>
        <p className={styles.heroSubtitle}>
          Insights, updates, and resources for your health journey
        </p>
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loadingState}>Loading posts...</div>
        ) : loadError ? (
          <div className={styles.errorState} role="alert">
            <p>{loadError}</p>
            <button type="button" className={styles.retryButton} onClick={loadPosts}>
              Try again
            </button>
          </div>
        ) : posts.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No blog posts yet. Check back soon.</p>
          </div>
        ) : (
          <div className={styles.grid}>
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className={styles.card}
              >
                {post.coverImageUrl ? (
                  <div className={styles.cardImage}>
                    <img loading="lazy" src={post.coverImageUrl} alt={post.title} />
                  </div>
                ) : (
                  <div className={styles.cardImagePlaceholder}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                  </div>
                )}
                <div className={styles.cardBody}>
                  <span className={styles.cardDate}>{formatDate(post.publishedAt)}</span>
                  <h2 className={styles.cardTitle}>{post.title}</h2>
                  {post.excerpt && (
                    <p className={styles.cardExcerpt}>{post.excerpt}</p>
                  )}
                  <span className={styles.readMore}>Read more</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogListPage;
